import {
  DynamoDBClient,
  QueryCommand,
  UpdateItemCommand,
  DeleteItemCommand,
  PutItemCommand,
  DescribeTableCommand,
  CreateTableCommand
} from '@aws-sdk/client-dynamodb';
import { SESClient, SendEmailCommand } from '@aws-sdk/client-ses';
import getAwsConfig from '../awsconfig';
import { v4 as uuidv4 } from 'uuid';
import { getSettings } from '../utils/settings';

const TABLE = 'notifications';

async function makeDynamo() {
  const cfg = await getAwsConfig();
  return new DynamoDBClient(cfg);
}

async function makeSES() {
  const cfg = await getAwsConfig();
  return new SESClient(cfg);
}

// Check if table exists and create if it doesn't
async function ensureTableExists() {
  const db = await makeDynamo();
  try {
    // Try to describe the table - will throw if it doesn't exist
    await db.send(new DescribeTableCommand({
      TableName: TABLE
    }));
    return true; // Table exists
  } catch (error) {
    if (error.name === 'ResourceNotFoundException') {
      // Table doesn't exist, create it
      try {
        await db.send(new CreateTableCommand({
          TableName: TABLE,
          KeySchema: [
            { AttributeName: 'userId', KeyType: 'HASH' },
            { AttributeName: 'notificationId', KeyType: 'RANGE' }
          ],
          AttributeDefinitions: [
            { AttributeName: 'userId', AttributeType: 'S' },
            { AttributeName: 'notificationId', AttributeType: 'S' }
          ],
          ProvisionedThroughput: {
            ReadCapacityUnits: 5,
            WriteCapacityUnits: 5
          }
        }));
        console.debug(`Created table: ${TABLE}`);
        return true;
      } catch (createError) {
        console.error('Error creating table:', createError);
        return false;
      }
    }
    console.error('Error checking table:', error);
    return false;
  }
}

export async function listNotifications(userId) {
  await ensureTableExists();
  const db = await makeDynamo();
  
  try {
    const resp = await db.send(new QueryCommand({
      TableName: TABLE,
      KeyConditionExpression: 'userId = :u',
      ExpressionAttributeValues: {
        ':u': { S: userId }
      },
      ScanIndexForward: false,
      Limit: 50
    }));

    return (resp.Items || []).map(item => ({
      notificationId: item.notificationId.S,
      itemType:       item.itemType.S,
      itemId:         item.itemId.S,
      message:        item.message.S,
      imageUrl:       item.imageUrl?.S,
      name:           item.name?.S || '',
      description:    item.description?.S || '',
      createdAt:      item.createdAt.S,
      read:           item.read.BOOL
    }));
  } catch (error) {
    console.error('Error listing notifications:', error);
    return [];
  }
}

export async function markAsRead(userId, notificationId) {
  await ensureTableExists();
  const db = await makeDynamo();
  
  try {
    await db.send(new UpdateItemCommand({
      TableName: TABLE,
      Key: {
        userId:         { S: userId },
        notificationId: { S: notificationId }
      },
      UpdateExpression: 'SET #r = :true',
      ExpressionAttributeNames:  { '#r': 'read' },
      ExpressionAttributeValues: { ':true': { BOOL: true } }
    }));
  } catch (error) {
    console.error('Error marking notification as read:', error);
  }
}

export async function clearNotification(userId, notificationId) {
  await ensureTableExists();
  const db = await makeDynamo();
  
  try {
    await db.send(new DeleteItemCommand({
      TableName: TABLE,
      Key: {
        userId:         { S: userId },
        notificationId: { S: notificationId }
      }
    }));
  } catch (error) {
    console.error('Error clearing notification:', error);
  }
}

/**
 * Creates an in‑app notification AND emails your admin address via SES.
 */
export async function createNotification(
  userId,
  { itemType, itemId, message, imageUrl, name, description }
) {
  await ensureTableExists();
  const db = await makeDynamo();
  const now = new Date().toISOString();
  const notificationId = uuidv4();

  try {
    // 1) Write to DynamoDB including name/description/imageUrl
    const item = {
      userId:         { S: userId },
      notificationId: { S: notificationId },
      itemType:       { S: itemType },
      itemId:         { S: itemId },
      message:        { S: message },
      createdAt:      { S: now },
      read:           { BOOL: false }
    };
    if (imageUrl)    item.imageUrl    = { S: imageUrl };
    if (name)        item.name        = { S: name };
    if (description) item.description = { S: description };

    await db.send(new PutItemCommand({
      TableName: TABLE,
      Item: item
    }));
  } catch (error) {
    console.error('Error creating notification in DynamoDB:', error);
  }

  // 2) SES email
  try {
    const ses = await makeSES();
    const settings = getSettings();
    const recipient = settings.notification?.recipient || 'admin@homegrown-genetics.com';

    await ses.send(new SendEmailCommand({
      Destination: { ToAddresses: [recipient] },
      Message: {
        Subject: { Data: 'Homegrown Genetics Inventory Alert', Charset: 'UTF-8' },
        Body: {
          Html: {
            Data: `
              <html><body style="font-family:Arial,sans-serif;line-height:1.5;">
                <h2>Inventory Alert</h2>
                <p><strong>Message:</strong> ${message}</p>
                <p><strong>Name:</strong> ${name}</p>
                <p><strong>Description:</strong> ${description}</p>
                <p><strong>Time:</strong> ${now}</p>
                ${
                  imageUrl
                    ? `<p><img src="${imageUrl}" alt="${name}"
                              style="max-width:100%; height:auto; border:1px solid #ddd;"/></p>`
                    : ''
                }
              </body></html>
            `,
            Charset: 'UTF-8'
          }
        }
      },
      Source: recipient
    }));
  } catch (err) {
    console.error('Failed to send SES email:', err);
  }

  return notificationId;
}
